import { Injectable } from "@angular/core";
import { CellStateStyle, StyleEdgeStyleValue } from "@maxgraph/core";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import {
    BLACK_COLOR,
    CIRCUIT_GRAY,
    CIRCUIT_GRAY_2,
    CIRCUIT_RED,
    TRANSPARENT_COLOR
} from "@nms-ng2/app/shared/constants/colors";
import { AnyDrawCommand, CellKind } from "@nms-ng2/app/shared/services/layout-manager/drawing.models";
import {
    AlignmentConfig,
    HorizontalAlignment,
    LayoutDirection,
    SimpleLayoutElement,
    SizingType,
    VerticalAlignment,
} from "@nms-ng2/app/shared/services/layout-manager/layout-manager.models";
import { LayoutManager } from "@nms-ng2/app/shared/services/layout-manager/layout-manager.service";
import {
    AMPLIFIER_MIDDLE_SPACE,
    CIRCUIT_LINK_CONFIGURED_STYLE,
    CIRCUIT_LINK_NOT_CONFIGURED_STYLE,
    DEFAULT_GAP,
    DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT,
    GAP_BETWEEN_TRANSPONDERS,
    MINIMUM_SIZE,
    PADDING_10,
    PADDING_10_LEFT_RIGHT,
    PADDING_10_TOP_BOTTOM,
    POSITION_TRANSMISSION_MAP,
    SINGLE_INTERMEDIATE_SPACE,
    TITLE_HEIGHT,
    TOPOLOGY_SETTINGS
} from "../models/diagram.constants";
import {
    AmplifierDirectionMap,
    AmplifierElement,
    CircuitDiagram,
    CircuitDirection,
    CircuitElement,
    CircuitElementState,
    CircuitElementType,
    DeviceElement,
    DeviceType,
    EndpointDeviceElement,
    EndpointType,
    HorizontalPosition,
    IntermediateDeviceElement,
    LinkElement,
    MuxElement,
    OscElement,
    PointsReposition,
    TransmissionLinkMap,
    TransponderElement
} from "../models/diagram.models";
import { DwdmCircuitDiagramUtilsService } from "../utils/dwdm-circuit-diagram-utils.service";
import { CircuitElementParameters } from "./circuit-element-parameters";
import { CircuitLayoutUtils } from "./circuit-layout-utils";

/**
 * Classe responsável por criar os comandos de desenho para um diagrama de circuito.
 */
@Injectable({
    providedIn: "root"
})
export class CircuitLayoutBuilder {
    private readonly layout: LayoutManager;

    constructor(private readonly diagramUtils: DwdmCircuitDiagramUtilsService) {
        this.layout = new LayoutManager();
    }

    /**
     * Cria os comandos de desenho para o diagrama de um dado circuito.
     *
     * Inicializa algumas propriedades do CircuitDiagram que não são exigidas para quem cria o modelo.
     *
     * @param circuitDiagram modelo contendo as informações do circuito que será desenhado.
     * @param rootParameters parâmetros opcionais para o elemento raiz do diagrama.
     * @returns Array ordenado com todos os comandos necessário para desenhar o diagrama do circuito passado.
     */
    createCircuitCommands(circuitDiagram: CircuitDiagram, rootParameters?: SimpleLayoutElement): AnyDrawCommand[] {
        if (!circuitDiagram) {
            return [];
        }

        this.setup(circuitDiagram);

        return this.buildFullCircuit(circuitDiagram, rootParameters);
    }

    /**
     * Inicializa algumas propriedades do CircuitDiagram que não são exigidas para quem cria o modelo,
     * como as opções de exibir ou não os links internos e externos, o array de links, e o mapeamento,
     * de posição das transmissões RX e TX.
     *
     * @param circuitDiagram modelo contendo as informações do circuito que será desenhado.
     */
    private setup(circuitDiagram: CircuitDiagram) {
        circuitDiagram.links = circuitDiagram.links ?? [];

        circuitDiagram.showExternalLinks = circuitDiagram.showExternalLinks ?? true;
        circuitDiagram.aEnd.showInternalLinks = circuitDiagram.aEnd.showInternalLinks ?? true;
        circuitDiagram.zEnd.showInternalLinks = circuitDiagram.zEnd.showInternalLinks ?? true;

        for (const transponder of [...circuitDiagram.aEnd.transponders, ...circuitDiagram.zEnd.transponders]) {
            transponder.state = transponder.state || CircuitElementState.INITIAL;
        }

        if (circuitDiagram.aEnd.mux) {
            circuitDiagram.aEnd.mux.state = circuitDiagram.aEnd.mux.state || CircuitElementState.INITIAL;
        }

        if (circuitDiagram.zEnd.mux) {
            circuitDiagram.zEnd.mux.state = circuitDiagram.zEnd.mux.state || CircuitElementState.INITIAL;
        }

        circuitDiagram.aEnd.transmissionPosition = {
            right: { TX: { }, RX: { } }
        }

        circuitDiagram.zEnd.transmissionPosition = {
            left: { TX: { }, RX: { } }
        }

        for (const intermediate of circuitDiagram.intermediates) {
            intermediate.showInternalLinks = intermediate.showInternalLinks ?? true;

            intermediate.transmissionPosition = {
                left:  { TX: { }, RX: { } },
                right: { TX: { }, RX: { } }
            }
        }
    }

    /**
     * Constrói os comandos para um diagrama completo de um circuito usando o LayoutManager.
     *
     * Primeiramente são definidas as propriedades do elemento raiz do diagrama.
     * Depois começa a criar em ordem os elementos do circuito como filhos do elemento raiz.
     *
     * @param circuitDiagram modelo contendo as informações do circuito que será desenhado.
     * @param rootParameters parâmetros opcionais para o elemento raiz do diagrama.
     * @returns Array ordenado com todos os comandos necessário para desenhar o diagrama do circuito passado.
     */
    private buildFullCircuit(circuitDiagram: CircuitDiagram, rootParameters?: SimpleLayoutElement): AnyDrawCommand[] {
        const rootElement = rootParameters || DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT;

        return this.layout.createLayout({...rootElement}, () => {
            // Cria A-END
            this.createEnd(circuitDiagram.aEnd);

            // Espaçador
            this.createGrowingSpacer();

            // Cria todos equipamentos intermediários e os espaçadores necessários
            this.createAllIntermediates(circuitDiagram.intermediates);

            // Cria Z-END
            this.createEnd(circuitDiagram.zEnd);

            // Cria Links
            this.createAllExternalLinks(circuitDiagram);
        });
    }

    /**
     * Cria um espaçador de tamanho fixo.
     *
     * @param width largura do elemento
     * @param height altura do elemento
     * @param fillColor cor do elemento
     */
    private createFixedSpacer(width: number, height: number = DEFAULT_GAP, fillColor: string = TRANSPARENT_COLOR) {
        const params: SimpleLayoutElement = { name: "fixed spacer", width, height, style: { fillColor } };

        this.layout.createElement(params);
    }

    /**
     * Cria um espaçador que cresce conforme o espaço disponível a ele.
     *
     * @param fillColor cor do elemento.
     */
    private createGrowingSpacer(fillColor: string = TRANSPARENT_COLOR) {
        const style = { fillColor };
        const params: SimpleLayoutElement = { name: "growing spacer", maxHeight: DEFAULT_GAP, sizing: SizingType.GROW, style };

        this.layout.createElement(params);
    }

    /**
     * Cria todos os elementos de layout internos e relativos a um equipamento endpoint (i.e. A-End ou Z-End).
     *
     * Importante lembrar que os elementos são criados em ordem e, por padrão, da esquerda para direita.
     * De acordo com o tipo de endpoint (i.e. A-END, Z-END) decide qual ordem deveria desenhar os elementos do layout.
     *
     * Elementos internos:
     *     - Transponders
     *     - Mux
     *     - Amplificadores
     *     - Links internos
     *
     * Elementos relativos:
     *     - OSC
     *     - RX/TX
     *     - Elementos invisíveis de link (usados para conexão dos links externos e internos)
     *
     * @param end equipamento endpoint de um circuito (i.e. A-End ou Z-End).
     */
    private createEnd(end: EndpointDeviceElement<EndpointType>) {
        end.type = DeviceType.END;

        const deviceParams = CircuitElementParameters.buildDeviceParameters(end);
        const namePrefix = `${end.endpointType} (${end.name})`;

        // Como os elementos são desenhados da esquerda para direita, a primeira coisa que precisamos fazer para desenhar um
        // endpoint é saber se precisamos desenhar os elementos que ficam a esquerda do equipamento, e isso é necessário
        // apenas quando o equipamento é um Z-END.
        if (end.endpointType === EndpointType.Z_END) {
            this.createRxTx("left", end, deviceParams.height);
            this.createInvisibleLinkElements("left", end, deviceParams.height);
        }

        end.element = this.layout.createElement({id: end.id, name: namePrefix, style: deviceParams.style }, () => {
            const sizing: SizingType = SizingType.GROW;
            const outsideName = `${namePrefix} - outside container`;
            const insideName = `${namePrefix} - inside container`;

            // Semelhante a lógica do RxTx que precisa considerar se for Z-END primeiro
            // pois os elementos são criados da esquerda para direita.
            //
            // A particularidade deste caso é que ele precisa ser feito depois de criar
            // o elemento com a imagem do equipamento para sobrepor ela corretamente.
            // Por isso que OSC é um filho do elemento com a imagem do device. Outra forma de resolver
            // isso seria implementar um z-index no Layout Manager, mas isso não é suportado no momento.
            if (end.endpointType === EndpointType.Z_END) {
                this.createOsc(end.osc, deviceParams.height);
            }

            this.layout.createElement({ name: outsideName, direction: LayoutDirection.TOP_TO_BOTTOM, sizing }, () => {
                this.createDeviceTitle(end, deviceParams, namePrefix);

                // Cria os elementos internos do equipamento.
                // Se for A-END, cria eles numa ordem, se for Z-END cria eles na ordem inversa.
                // As funções verificam internamente se os elementos internos (e.g. Mux, Amplificadores)
                // existem. Se não existirem os espaçadores vão tomar todo espaço restante.
                this.layout.createElement({ name: insideName, padding: PADDING_10, sizing }, () => {
                    if (end.endpointType === EndpointType.A_END) {
                        this.createTranspondersGroup(end.endpointType, end.transponders);
                        this.createMux(end.endpointType, end.mux);
                        this.createGrowingSpacer();
                        this.createAmplifiersGroup(end.amplifiers, DeviceType.END);
                        this.createGrowingSpacer();
                    } else {
                        this.createGrowingSpacer();
                        this.createAmplifiersGroup(end.amplifiers, DeviceType.END);
                        this.createGrowingSpacer();
                        this.createMux(end.endpointType, end.mux);
                        this.createTranspondersGroup(end.endpointType, end.transponders);
                    }
                });
            });

            if (end.endpointType === EndpointType.A_END) {
                this.createOsc(end.osc, deviceParams.height);
            }
        });

        if (end.endpointType === EndpointType.A_END) {
            this.createInvisibleLinkElements("right", end, deviceParams.height);
            this.createRxTx("right", end, deviceParams.height);
        }

        // Ao final da criação do equipamento endpoint, são criados os elementos
        // para os links internos. Isto precisa ser feito depois de criar os outros
        // elementos do equipamento, principalmente os elementos invisíveis de links.
        if (end.showInternalLinks) {
            this.createInternalLinksForEndDevice(end);
        }
    }

    /**
     * Cria o elemento de layout para o título de um equipamento (endpoint ou intermediário).
     *
     * @param device equipamento em questão
     * @param deviceParams elemento de layout que define os parâmetros de um equipamento.
     * @param namePrefix string utilizada para nomear internamente o elemento de layout.
     */
    private createDeviceTitle(device: DeviceElement, deviceParams: SimpleLayoutElement, namePrefix: string) {
        const titleName = `${namePrefix} title`;

        this.layout.createElement({
            name: titleName,
            value: device.name,
            width: deviceParams.width,
            height: TITLE_HEIGHT,
            custom: {
                getCustomTooltip: device.getCustomTooltip
            }
        });
    }

    /**
     * Cria os elementos de layout do conjunto de transponders de um determinado endpoint.
     *
     * Como os transponders precisam ser desenhados verticalmente, aqui é criado
     * um elemento agrupador com o a direção definida como "TOP_TO_BOTTOM".
     *
     * @param endpointType tipo do endpoint (i.e. A-END, Z-END).
     * @param transponders array com todos Transponders do endpoint em questão.
     */
    private createTranspondersGroup(endpointType: EndpointType, transponders: TransponderElement[]) {
        const name = `${endpointType} transponders container group`;
        const childGap: number = GAP_BETWEEN_TRANSPONDERS;

        this.layout.createElement({ name, direction: LayoutDirection.TOP_TO_BOTTOM, childGap }, () => {
            for (const transponder of transponders) {
                this.createTransponder(endpointType, transponder);
            }
        });
    }

    /**
     * Cria o elemento de layout de um transponder específico.
     *
     * De acordo com o tipo de endpoint (i.e. A-END, Z-END) decide se deveria
     * criar primeiro o label ou a imagem do transponder e também se a imagem deveria
     * ser espelhada.
     *
     * @param endpointType tipo do endpoint (i.e. A-END, Z-END).
     * @param transponder um transponder específico.
     */
    private createTransponder(endpointType: EndpointType, transponder: TransponderElement) {
        const transponderParams = CircuitElementParameters.buildTransponderParameters(transponder);
        const name = `${endpointType} transponder ${transponder.multirate}`;
        const labelGap = 8;

        transponderParams.custom = {
            buildPopover: transponder.buildPopover,
            popoverData: transponder.popoverData
        };

        this.layout.createElement({ name: `${name} - container` }, () => {
            if (endpointType === EndpointType.A_END) {
                this.createTransponderLabel(transponder, transponderParams);
                this.createFixedSpacer(labelGap);
                transponder.element = this.layout.createElement({ name, id: transponder.id, ...transponderParams });
            } else {
                const flipped = CircuitLayoutUtils.flip(transponderParams);
                transponder.element = this.layout.createElement({ name, id: transponder.id, ...flipped });
                this.createFixedSpacer(labelGap);
                this.createTransponderLabel(transponder, transponderParams);
            }
        });
    }

    /**
     * Cria o elemento de layout do label de um transponder específico.
     *
     * @param transponder um transponder específico.
     * @param transponderParams parâmetros que vem do elemento de layout de um transponder.
     */
    private createTransponderLabel(transponder: TransponderElement, transponderParams: SimpleLayoutElement,) {
        const transponderLabel: SimpleLayoutElement = {
            name: `transponder label ${transponder.multirate}`,
            value: transponder.multirate,
            width: 60,
            height: transponderParams.height,
            style: { fontStyle: 0 },
            padding: { right: 10 }
        };

        if (transponder.state !== CircuitElementState.CONFIGURED) {
            transponderLabel.style.fontColor = CIRCUIT_GRAY;
        }

        this.layout.createElement({ ...transponderLabel });
    }

    /**
     * Cria o elemento de layout de um MUX/DEMUX específico, caso exista.
     *
     * De acordo com o tipo de endpoint (i.e. A-END, Z-END) decide se deveria
     * criar primeiro um espaçador ou a imagem do MUX/DEMUX e também se a imagem deveria
     * ser espelhada.
     *
     * @param endpointType tipo do endpoint (i.e. A-END, Z-END).
     * @param mux um MUX/DEMUX específico.
     */
    private createMux(endpointType: EndpointType, mux: MuxElement) {
        if (mux) {
            const name = `${endpointType} mux`;
            const parameters = CircuitElementParameters.buildMuxParameters(mux);
            const alignment = { vertical: VerticalAlignment.MIDDLE, horizontal: HorizontalAlignment.CENTER };
            const muxGap = 30; // Gap necessário entre transponder e mux

            this.layout.createElement({ name: `${name} - container`, alignment, sizing: SizingType.GROW }, () => {
                if (endpointType === EndpointType.A_END) {
                    this.createFixedSpacer(muxGap);
                    mux.element = this.layout.createElement({ name, id: mux.id, ...parameters,  });
                } else {
                    const flipped = CircuitLayoutUtils.flip(parameters);
                    mux.element = this.layout.createElement({ name, id: mux.id, ...flipped });
                    this.createFixedSpacer(muxGap);
                }
            });
        }
    }

    /**
     * Cria os elementos de layout do conjunto de amplificadores de
     * um determinado equipamento (endpoint ou intermediário), caso exista.
     *
     * Como os amplificadores precisam ser desenhados verticalmente, aqui é criado
     * um elemento agrupador com o a direção definida como "TOP_TO_BOTTOM".
     *
     * @param amplifiersMap mapeamento dos amplificadores de acordo com a direção deles no circuito.
     * @param deviceType tipo que indica se é um endpoint ou um intermediário (que pode ser menor ou completo).
     */
    private createAmplifiersGroup(amplifiersMap: AmplifierDirectionMap, deviceType: DeviceType) {
        if (amplifiersMap) {
            const name = "amplifiers container group";
            const padding = PADDING_10_TOP_BOTTOM;
            const topToBottom = LayoutDirection.TOP_TO_BOTTOM;
            const amplifiers = Object.values(amplifiersMap);
            const hasLabel = !!amplifiers[0].name;
            const hasBothAmplifiers = amplifiers.length === 2;
            const isOnlyAmpOnEndpointBottom = CircuitLayoutUtils.isOnlyAmpOnEndpointBottom(amplifiersMap, deviceType);

            this.layout.createElement({ name, padding, direction: topToBottom }, () => {
                const topSpace = (deviceType === DeviceType.INTERMEDIATE_HALF) ? 8 : 25;
                const middleSpace = (hasLabel) ? AMPLIFIER_MIDDLE_SPACE - TITLE_HEIGHT : AMPLIFIER_MIDDLE_SPACE;

                this.createFixedSpacer(DEFAULT_GAP, topSpace);

                if (isOnlyAmpOnEndpointBottom) {
                    this.createFixedSpacer(DEFAULT_GAP, 72);
                }

                this.createAmplifier(amplifiersMap[CircuitDirection.TOWARDS_Z_END]);

                if (hasBothAmplifiers) {
                    this.createFixedSpacer(DEFAULT_GAP, middleSpace);
                }

                this.createAmplifier(amplifiersMap[CircuitDirection.TOWARDS_A_END]);
            });
        }
    }

    /**
     * Cria o elemento de layout de um amplificador específico, caso exista.
     *
     * @param amplificador um amplificador específico.
     */
    private createAmplifier(amplifier: AmplifierElement) {
        if (amplifier) {
            const name = `Amp ${amplifier.name}`;

            let parameters = CircuitElementParameters.buildAmplifierParameters(amplifier);

            parameters.custom = {
                popoverData: amplifier.popoverData,
                buildPopover: amplifier.buildPopover
            };

            if (amplifier.direction === CircuitDirection.TOWARDS_A_END) {
                parameters = CircuitLayoutUtils.flip(parameters);
            }

            amplifier.element = this.layout.createElement({ name, id: amplifier.id, ...parameters });

            this.createAmplifierLabel(amplifier);
        }
    }

    /**
     * Cria o elemento de layout do label de um amplificador específico,
     * caso o nome do amplificador estiver definido.
     *
     * @param amplificador um amplificador específico.
     */
    private createAmplifierLabel(amplifier: AmplifierElement) {
        if (amplifier.name) {
            const value = amplifier.name;

            const label: SimpleLayoutElement = {
                name: `amplifier label (${value})`,
                value,
                width: amplifier.element.width,
                height: TITLE_HEIGHT,
                style: { fontStyle: 0 }
            };

            if (amplifier.state !== CircuitElementState.CONFIGURED) {
                label.style.fontColor = CIRCUIT_RED;
            }

            this.layout.createElement({ ...label });
        }
    }

    /**
     * Cria os elementos de layout para todos equipamentos intermediários, caso existirem, e os espaçadores necessários.
     *
     * @param intermediates todos equipamentos intermediários presentes no circuito.
     */
    private createAllIntermediates(intermediates: IntermediateDeviceElement[]) {
        for (const intermediate of intermediates) {
            if (intermediate.amplifiers && Object.values(intermediate.amplifiers).length === 1) {
                intermediate.type = DeviceType.INTERMEDIATE_HALF;
                const direction: CircuitDirection = Object.keys(intermediate.amplifiers)[0] as CircuitDirection;
                const name = `single intermediate wrapper (${intermediate.name})`;

                this.layout.createElement({ name, direction: LayoutDirection.TOP_TO_BOTTOM }, () => {
                    const data = TOPOLOGY_SETTINGS[CircuitElementType.DEVICE][intermediate.type];
                    const heightSpace = (direction === CircuitDirection.TOWARDS_Z_END)
                        ? SINGLE_INTERMEDIATE_SPACE
                        : data.height - SINGLE_INTERMEDIATE_SPACE;

                    // Gap necessário para posicionar o intermediário menor de acordo com a direção do circuito
                    this.createFixedSpacer(data.width, heightSpace);

                    this.createIntermediate(intermediate, DeviceType.INTERMEDIATE_HALF, direction);
                });
            } else {
                intermediate.type = DeviceType.INTERMEDIATE_FULL;
                this.createIntermediate(intermediate, DeviceType.INTERMEDIATE_FULL);
            }

            this.createGrowingSpacer();
        }
    }

    /**
     * Cria o elemento de layout de um equipamento intermediário específico.
     *
     * @param intermediate equipamento intermediário específico
     * @param intermediateKind tipo que indica se trata de um intermediário menor ou completo.
     * @param direction direção do circuito, necessário apenas quando se trata de um intermediaŕio menor
     */
    private createIntermediate(intermediate: IntermediateDeviceElement, intermediateKind: DeviceType,
            direction?: CircuitDirection) {
        const deviceParams = CircuitElementParameters.buildDeviceParameters(intermediate);
        const namePrefix = `intermediate (${intermediate.name})`;

        const isIntermediateFull = intermediateKind === DeviceType.INTERMEDIATE_FULL;
        const createBothRxAndTx = isIntermediateFull;
        const isMiddleAligned = isIntermediateFull;

        this.layout.createElement({ name: `${namePrefix} - external container` }, () => {
            this.createRxTx("left", intermediate, deviceParams.height, direction, createBothRxAndTx);
            this.createInvisibleLinkElements("left", intermediate, deviceParams.height, direction, createBothRxAndTx);

            intermediate.element = this.layout.createElement({ name: namePrefix, id: intermediate.id, ...deviceParams }, () => {
                const outsideName = `${namePrefix} - outside container`;
                const insideName = `${namePrefix} - inside container`;

                this.createOsc(intermediate.oscPosition?.left, deviceParams.height, isMiddleAligned);

                this.layout.createElement({ name: outsideName, direction: LayoutDirection.TOP_TO_BOTTOM }, () => {
                    this.createDeviceTitle(intermediate, deviceParams, namePrefix);

                    this.layout.createElement({ name: insideName, padding: PADDING_10, sizing: SizingType.GROW }, () => {
                        // Centraliza horizontalmente os amplificadores criando espaçadores antes e depois
                        this.createGrowingSpacer();
                        this.createAmplifiersGroup(intermediate.amplifiers, intermediateKind);
                        this.createGrowingSpacer();
                    });
                });

                this.createOsc(intermediate.oscPosition?.right, deviceParams.height, isMiddleAligned);
            });

            this.createInvisibleLinkElements("right", intermediate, deviceParams.height, direction, createBothRxAndTx);
            this.createRxTx("right", intermediate, deviceParams.height, direction, createBothRxAndTx);
        });

        this.createInternalLinksForIntermediateDevice(intermediate);
    }

    /**
     * Cria o elemento de layout de um OSC específico, caso exista.
     *
     * @param osc elemento OSC em questão.
     * @param deviceHeight altura do device em questão.
     * @param isMiddleAligned flag para dizer se deveria ser alinhado verticalmente no centro.
     */
    private createOsc(osc: OscElement, deviceHeight: number, isMiddleAligned = true) {
        if (osc) {
            const oscParameters = CircuitElementParameters.buildOscParameters(osc);

            const middleAlignment = (isMiddleAligned) ? VerticalAlignment.MIDDLE : VerticalAlignment.BOTTOM;
            const alignment: AlignmentConfig = { vertical: middleAlignment, horizontal: HorizontalAlignment.LEFT};

            const offsetX = (oscParameters.width/2) * (-1);
            const offsetY = TITLE_HEIGHT;
            const width = MINIMUM_SIZE;
            const height = deviceHeight - TITLE_HEIGHT;

            const value = osc.name;
            const name = `OSC ${osc.name} - container`;

            oscParameters.custom = {
                buildPopover: osc.buildPopover,
                popoverData: osc.popoverData
            };

            this.layout.createElement({ name, isFloating: true, offset: { y: offsetY}, alignment, width, height }, () => {
                this.layout.createElement({ name: value, value, offset: { x: offsetX, y: offsetY}, ...oscParameters })
            });
        }
    }

    /**
     * Cria os elementos de layout para os labels RX e TX.
     *
     * @param position indica a posição dos labels, se deveriam ser criados a esquerda ou a direita do equipamento.
     * @param device equipamento que receberá os labels RX/TX.
     * @param deviceHeight altura do device em questão.
     * @param direction direção do circuito, necessário apenas quando se trata de um intermediaŕio menor
     * @param createBoth flag que indica se deveria cria o par RX/TX ou apenas um deles,
     *                   necessário apenas quando se trata de um intermediaŕio menor.
     */
    private createRxTx(position: HorizontalPosition, device: DeviceElement, deviceHeight: number,
            direction?: CircuitDirection, createBoth: boolean = true): void {
        const horizontalAlignment = (position === "right") ? HorizontalAlignment.LEFT : HorizontalAlignment.RIGHT;
        const alignment: AlignmentConfig = {vertical: VerticalAlignment.MIDDLE, horizontal: horizontalAlignment};
        const topToBottom = LayoutDirection.TOP_TO_BOTTOM;

        const outsideContainerParams = {
            name: "RX/TX - outside container",
            direction: topToBottom,
            width: 50,
            height: deviceHeight
        };

        this.layout.createElement(outsideContainerParams, () => {
            // Simula o tamanho do título do equipamento para permitir alinhar RX/TX no container abaixo
            this.createFixedSpacer(MINIMUM_SIZE, TITLE_HEIGHT);

            const insideContainerParams = {
                name: "RX/TX - inside container",
                direction: topToBottom,
                padding: PADDING_10_LEFT_RIGHT,
                alignment,
                sizing: SizingType.GROW
            };

            this.layout.createElement(insideContainerParams, () => {
                const transmissions = POSITION_TRANSMISSION_MAP[position];
                const fontColor = (device.state === CircuitElementState.INITIAL) ? CIRCUIT_GRAY : BLACK_COLOR;
                const rxTxParams = { width: 16, height: 16, style: { fontColor } }

                // Se ambos labels precisam ser criados, então basta saber a posição (i.e. left, right)
                // e pegar do mapeamento `POSITION_TRANSMISSION_MAP` para obter corretamente
                // qual é label superior e qual é o inferior.
                // Mas se apenas um deles precisa ser criado, que acontece no caso dos intermediários menores,
                // então é preciso saber também a direção do circuito.
                if (createBoth) {
                    this.layout.createElement({ name: transmissions.top, value: transmissions.top, ...rxTxParams});
                    this.createFixedSpacer(MINIMUM_SIZE, 80); // Gap necessário entre RX e TX
                    this.layout.createElement({ name: transmissions.bottom, value: transmissions.bottom, ...rxTxParams});
                } else {
                    const label: Transmission = CircuitLayoutUtils.getTransmissionFromDirection(position, direction);

                    this.layout.createElement({ name: label, value: label, ...rxTxParams});
                    this.createFixedSpacer(MINIMUM_SIZE, 24); // Gap necessário para que RX ou TX não fiquem em cima dos links
                }
            });
        });
    }

    /**
     * Cria elementos de layout invisíveis correspondentes ao ponto de ligação RX/TX.
     * São utilizados para criação dos links: todos links externos,
     * e para alguns links internos (os que terminam em RX/TX).
     *
     * @param position indica a posição dos elementos, se deveriam ser criados a esquerda ou a direita do equipamento.
     * @param device equipamento que receberá os elementos invisíveis.
     * @param deviceHeight altura do device em questão.
     * @param direction direção do circuito, necessário apenas quando se trata de um intermediaŕio menor
     * @param createBoth flag que indica se deveria cria o par de elementos invisíveis ou apenas um deles,
     *                   necessário apenas quando se trata de um intermediaŕio menor.
     */
    private createInvisibleLinkElements(position: HorizontalPosition, device: DeviceElement, deviceHeight: number,
            direction?: CircuitDirection, createBoth: boolean = true) {
        const horizontalAlignment = (position === "right") ? HorizontalAlignment.LEFT : HorizontalAlignment.RIGHT;
        const topToBottom = LayoutDirection.TOP_TO_BOTTOM;

        const transmissions = POSITION_TRANSMISSION_MAP[position]
        const topTransmission: Transmission = transmissions.top;
        const bottomTransmission: Transmission = transmissions.bottom;

        const alignment: AlignmentConfig = { vertical: VerticalAlignment.MIDDLE, horizontal: horizontalAlignment };

        const outsideContainerParams = {
            name: "RX/TX outside container",
            direction: topToBottom,
            width: MINIMUM_SIZE,
            height: deviceHeight
        };

        this.layout.createElement(outsideContainerParams, () => {
            // Simula o tamanho do título do equipamento
            this.createFixedSpacer(MINIMUM_SIZE, TITLE_HEIGHT);

            const insideContainerParams = {
                name: "invisible link elements - inside container",
                direction: topToBottom,
                alignment,
                sizing: SizingType.GROW
            };

            this.layout.createElement(insideContainerParams, () => {
                const transmissionMap = device.transmissionPosition[position];
                const topName = `invisible link element ('${device.name}' - ${position} - ${topTransmission})`;
                const bottomName = `invisible link element ('${device.name}' - ${position} - ${bottomTransmission})`;

                if (createBoth) {
                    transmissionMap[topTransmission].element = this.layout.createElement({ name: topName });
                    this.createFixedSpacer(MINIMUM_SIZE, 72); // Gap necessário entre elementos invisíveis
                    transmissionMap[bottomTransmission].element = this.layout.createElement({ name: bottomName });
                } else {
                    const transmission = CircuitLayoutUtils.getTransmissionFromDirection(position, direction);
                    transmissionMap[transmission].element = this.layout.createElement({ name: topName });
                }
            });
        });
    }

    /**
     * Cria os elementos de layout para os links internos de um Endpoint.
     *
     * @param end equipamento endpoint que deveria receber links internos.
     */
    private createInternalLinksForEndDevice(end: EndpointDeviceElement<EndpointType>) {
        const position: HorizontalPosition = (end.endpointType === EndpointType.A_END) ? "right" : "left";

        const transmissions = POSITION_TRANSMISSION_MAP[position];
        const transmissionMap: TransmissionLinkMap = end.transmissionPosition[position];
        const topTransmission: Transmission = transmissions.top;
        const bottomTransmission: Transmission = transmissions.bottom;

        if (end.mux) {
            const transponders = CircuitLayoutUtils
                .filterElementsByStates(end.transponders, [CircuitElementState.CONFIGURED, CircuitElementState.PRE_CONFIGURED]);

            for (const transponder of transponders) {
                const customStyle = { perimeterSpacing: 6 };
                const repositionYBasedOnSource: PointsReposition = { offset: 15, points: [0, 1], basedOn: "source"};
                const repositionYBasedOnTarget: PointsReposition = { offset: 25, points: [0, 1], basedOn: "target"};

                this.createInternalLink(transponder, end.mux, null, customStyle, null, repositionYBasedOnSource);
                this.createInternalLink(end.mux, transponder, null, customStyle, null, repositionYBasedOnTarget);
            }

            const remainingTransmissions = [ topTransmission, bottomTransmission ];

            if (end.amplifiers) {
                for (const amplifier of Object.values(end.amplifiers)) {
                    this.createInternalLink(amplifier, end.mux);

                    const transmission = CircuitLayoutUtils.getTransmissionFromDirection(position, amplifier.direction);
                    const index = remainingTransmissions.indexOf(transmission);
                    remainingTransmissions.splice(index, 1);

                    const targetElement = transmissionMap[transmission].element;
                    this.createInternalLink(amplifier, targetElement);
                }
            }

            for (const transmission of remainingTransmissions) {
                const targetElement = transmissionMap[transmission].element;
                this.createInternalLink(end.mux, targetElement);
            }
        } else {
            // Estamos pegando apenas o primeiro transponder configurado/pré-condigurado aqui porque, neste caso onde
            // não existe MUX, podemos supor que de fato deveria existir apenas um transponder (ou nenhum).
            const transponder = CircuitLayoutUtils
                    .findElementByStates(end.transponders, [CircuitElementState.CONFIGURED, CircuitElementState.PRE_CONFIGURED]);
            const remainingDirections = [ CircuitDirection.TOWARDS_Z_END, CircuitDirection.TOWARDS_A_END ];

            // TODO [Bug 264545] - Criar testes unitários para o cálculo de reposicionamento
            //                     dos links internos de transponders para amplificadores
            if (transponder) {
                const edgeStyle: StyleEdgeStyleValue = "elbowEdgeStyle";
                const customStyle: CellStateStyle = { perimeterSpacing: 3 }

                if (end.amplifiers) {
                    for (const [direction, amplifier] of Object.entries(end.amplifiers)) {
                        const repositionX: PointsReposition = (direction === CircuitDirection.TOWARDS_A_END)
                            ? this.diagramUtils.getTransponderToAmpLinkXAxisOffset(end, transponder)
                            : null;
                        const repositionY: PointsReposition = this.diagramUtils
                            .getTransponderToAmpLinkYAxisOffset(end, transponder, amplifier.direction);

                        this.createInternalLink(transponder, amplifier, edgeStyle, customStyle, repositionX, repositionY);

                        const transmission = CircuitLayoutUtils.getTransmissionFromDirection(position, amplifier.direction);
                        const directionIndex = remainingDirections.indexOf(amplifier.direction);
                        remainingDirections.splice(directionIndex, 1);

                        const targetElement = transmissionMap[transmission].element;
                        this.createInternalLink(amplifier, targetElement, null, customStyle);
                    }
                }

                // - Se não tiver sobrado direções, é porque tanto TX como RX estão
                // conectados em aplificadores e nada precisará ser feito (for iterando numa lista vazia).
                // - Se tiver sobrado duas direções, é porque não há amplificadores e ambos links precisam ser criados.
                // - Se tiver sobrado apenas uma direação, é porque a outra conecta num amplificador,
                // e nesse caso o offset da conexão que falta ser criada precisa ser um pouco diferente.
                const hasSiblingWithAmp = (remainingDirections.length === 1);

                for (const direction of remainingDirections) {
                    const transmission = CircuitLayoutUtils.getTransmissionFromDirection(position, direction);
                    const element = transmissionMap[transmission].element;

                    const repositionX = (hasSiblingWithAmp)
                        ? this.diagramUtils.getTransponderSiblingToAmpLinkXAxisOffset(end, transponder, direction)
                        : this.diagramUtils.getTransponderLinkXAxisOffset(end, transponder, direction);
                    const repositionY = this.diagramUtils.getTransponderLinkYAxisOffset(end, transponder, direction);

                    this.createInternalLink(transponder, element, edgeStyle, customStyle, repositionX, repositionY);
                }
            }
        }
    }

    /**
     * Cria os elementos de layout para os links internos de um equipamento intermediário.
     *
     * @param intermediate equipamento intermediário que deveria receber links internos.
     */
    private createInternalLinksForIntermediateDevice(intermediate: IntermediateDeviceElement) {
        if (intermediate.amplifiers) {
            for (const amplifier of Object.values(intermediate.amplifiers)) {
                this.createInternalLinkForIntermediateDeviceAmplifier(intermediate, amplifier, "left");
                this.createInternalLinkForIntermediateDeviceAmplifier(intermediate, amplifier, "right");
            }
        }
    }

    /**
     * Cria o elemento de layout para um link interno de um equipamento intermediário e seu amplificador.
     *
     * @param intermediate equipamento intermediário em questão.
     * @param amplifier amplificador contido no equipamento intermediário.
     * @param position indica a posição dos elementos
     */
    private createInternalLinkForIntermediateDeviceAmplifier(intermediate: IntermediateDeviceElement, amplifier: AmplifierElement,
            position: HorizontalPosition) {
        const transmission = CircuitLayoutUtils.getTransmissionFromDirection(position, amplifier.direction);
        const element = intermediate.transmissionPosition[position][transmission].element;

        this.createInternalLink(amplifier, element);
    }

    /**
     * Cria os elementos de layout para os links externos do circuito.
     *
     * @param circuitDiagram modelo contendo as informações do circuito que será desenhado.
     */
    private createAllExternalLinks(circuitDiagram: CircuitDiagram) {
        if (circuitDiagram.showExternalLinks) {
            const devices: DeviceElement[] = [ circuitDiagram.aEnd, circuitDiagram.zEnd, ...circuitDiagram.intermediates ];

            for (const link of circuitDiagram.links) {
                const sourceDevice = CircuitLayoutUtils.findElementById(devices, link.source.deviceIdentifier);
                const targetDevice = CircuitLayoutUtils.findElementById(devices, link.target.deviceIdentifier);

                this.createExternalLink(link, sourceDevice, targetDevice);
            }
        }
    }

    /**
     * Cria o elemento de layout para um link interno específico.
     *
     * @param source elemento de origem do link interno
     * @param target elemento de destino do link interno
     * @param edgeStyle estilo visual que será usado para o link
     */
    private createInternalLink(source: CircuitElement, target: CircuitElement, edgeStyle?: StyleEdgeStyleValue,
            customStyle?: CellStateStyle, repositionX?: PointsReposition, repositionY?: PointsReposition) {
        const params: SimpleLayoutElement = {
            source: source.id,
            target: target.id,
            style: { strokeColor: CIRCUIT_GRAY_2, ...customStyle },
            custom: {
                repositionX,
                repositionY
            }
        };

        if (edgeStyle) {
            params.style.edgeStyle = edgeStyle ;
        }

        this.createLink("internal link", params);
    }

    /**
     * Cria o elemento de layout para um link externo específico.
     *
     * @param link link externo a ser criado
     * @param sourceDevice equipamento de origem
     * @param targetDevice equipamento de destino
     */
    private createExternalLink(link: LinkElement, sourceDevice: DeviceElement, targetDevice: DeviceElement) {
        const isConfigured = link.state === CircuitElementState.CONFIGURED;
        const defaultStyle: CellStateStyle = isConfigured ? CIRCUIT_LINK_CONFIGURED_STYLE : CIRCUIT_LINK_NOT_CONFIGURED_STYLE;
        const style: CellStateStyle = { ...defaultStyle, deletable: link.canRemove };

        const position = CircuitLayoutUtils.getPositionFromDirection(link.direction);
        const oppositePosition = CircuitLayoutUtils.getOppositePosition(position);

        const source = sourceDevice.transmissionPosition[position][link.source.transmission]?.element;
        const target = targetDevice.transmissionPosition[oppositePosition][link.target.transmission]?.element;
        const linkName = link.name || undefined;

        if (source && target) {
            const linkParams: SimpleLayoutElement = {
                id: link.id,
                source: source.id,
                target: target.id,
                value: linkName,
                style,
                custom: {
                    hasAnimation: isConfigured,
                    onRemove: link.onRemove,
                    getCustomTooltip: link.getCustomTooltip,
                    buildPopover: link.buildPopover,
                    popoverData: link.popoverData
                }
            };

            this.createLink("external link", linkParams);
        } else {
            console.error(`Unable to create link for '${sourceDevice.name}' (${link.source.transmission})`
                                              + ` to '${targetDevice.name}' (${link.target.transmission})`);
        }
    }

    /**
     * Cria o elemento de layout para um link específico, seja interno ou externo.
     *
     * @param params parâmetros que vem do elemento de layout de um link.
     */
    private createLink(name: string, params: SimpleLayoutElement) {
        this.layout.createElement({ name, kind: CellKind.EDGE, ...params });
    }
}
